import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import {
    useSearchParams
} from 'react-router-dom';
import axios from 'axios';
import {
    Alert,
    Button,
    Card,
    Drawer,
    Input,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import {
    ReloadOutlined,
    SearchOutlined,
    WarningOutlined
} from '@ant-design/icons';
import Layout from './Layout';
import BitrixTaskDetailPanel from './BitrixTaskDetailPanel';

const { Title, Text } = Typography;

const STATUS_COLORS = {
    1: 'blue',
    2: 'default',
    3: 'processing',
    4: 'gold',
    5: 'success',
    6: 'orange',
    7: 'error'
};

const DEFAULT_STATUSES = {
    1: 'Новая',
    2: 'Ждёт выполнения',
    3: 'Выполняется',
    4: 'Ждёт контроля',
    5: 'Завершена',
    6: 'Отложена',
    7: 'Отклонена'
};

const DEFAULT_SCOPES = {
    active: 'Активные',
    all: 'Все',
    overdue: 'Просроченные',
    completed: 'Завершённые',
    deferred: 'Отложенные',
    declined: 'Отклонённые'
};

const DEFAULT_ROLES = {
    any: 'Любая роль',
    creator: 'Я постановщик',
    responsible: 'Я исполнитель',
    accomplice: 'Я соисполнитель',
    auditor: 'Я наблюдатель'
};

const BitrixTasksPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const openTaskId = searchParams.get('task')
        ? Number(searchParams.get('task'))
        : null;

    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
    const [scopes, setScopes] = useState(DEFAULT_SCOPES);
    const [roles, setRoles] = useState(DEFAULT_ROLES);
    const [accessInfo, setAccessInfo] = useState(null);
    const [qInput, setQInput] = useState('');
    const [q, setQ] = useState('');
    const [scope, setScope] = useState('active');
    const [status, setStatus] = useState(undefined);
    const [role, setRole] = useState('any');
    const [groupId, setGroupId] = useState(undefined);
    const [groups, setGroups] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [drawerTitle, setDrawerTitle] = useState('Задача');

    const openTask = useCallback(
        id => {
            if (!id) return;
            const next = new URLSearchParams(searchParams);
            next.set('task', String(id));
            setSearchParams(next, { replace: false });
            const row = items.find(t => Number(t.id) === Number(id));
            setDrawerTitle(
                row
                    ? `#${row.id} ${row.title || ''}`.trim()
                    : `Задача #${id}`
            );
        },
        [items, searchParams, setSearchParams]
    );

    const closeTask = useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.delete('task');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get('/api/bitrix-tasks', {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    scope: scope || 'active',
                    q: q || undefined,
                    status: status != null ? status : undefined,
                    role: role && role !== 'any' ? role : undefined,
                    group_id:
                        groupId != null ? groupId : undefined,
                    limit: pageSize,
                    offset: (page - 1) * pageSize
                }
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
            if (data.statuses) setStatuses(data.statuses);
            if (data.scopes) setScopes(data.scopes);
            if (data.roles) setRoles(data.roles);
            setAccessInfo(data.access || null);
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Не удалось загрузить задачи Bitrix'
            );
        } finally {
            setLoading(false);
        }
    }, [q, scope, status, role, groupId, page, pageSize]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        (async () => {
            try {
                const token = localStorage.getItem('token');
                const { data } = await axios.get(
                    '/api/bitrix-tasks/meta/groups',
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                setGroups(data.items || []);
            } catch (_) {
                /* ignore */
            }
        })();
    }, []);

    useEffect(() => {
        if (!openTaskId) return;
        const row = items.find(
            t => Number(t.id) === Number(openTaskId)
        );
        if (row) {
            setDrawerTitle(
                `#${row.id} ${row.title || ''}`.trim()
            );
        } else {
            setDrawerTitle(`Задача #${openTaskId}`);
        }
    }, [openTaskId, items]);

    const statusOptions = useMemo(
        () =>
            Object.entries(statuses).map(([id, name]) => ({
                value: Number(id),
                label: name
            })),
        [statuses]
    );

    const scopeOptions = useMemo(
        () =>
            Object.entries(scopes).map(([id, name]) => ({
                value: id,
                label: name
            })),
        [scopes]
    );

    const roleOptions = useMemo(
        () =>
            Object.entries(roles).map(([id, name]) => ({
                value: id,
                label: name
            })),
        [roles]
    );

    const groupOptions = useMemo(
        () =>
            (groups || []).map(g => ({
                value: Number(g.id),
                label: g.tasks_count
                    ? `${g.name} (${g.tasks_count})`
                    : g.name
            })),
        [groups]
    );

    const applySearch = () => {
        setPage(1);
        setQ(qInput.trim());
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 80,
            render: id => (
                <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={e => {
                        e.stopPropagation();
                        openTask(id);
                    }}
                >
                    {id}
                </Button>
            )
        },
        {
            title: 'Название',
            dataIndex: 'title',
            ellipsis: true,
            render: (title, row) => (
                <Space>
                    {row.is_overdue ? (
                        <WarningOutlined
                            style={{ color: '#cf1322' }}
                        />
                    ) : null}
                    <Button
                        type="link"
                        style={{
                            padding: 0,
                            height: 'auto',
                            whiteSpace: 'normal',
                            textAlign: 'left'
                        }}
                        onClick={e => {
                            e.stopPropagation();
                            openTask(row.id);
                        }}
                    >
                        {title || 'Без названия'}
                    </Button>
                </Space>
            )
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            width: 170,
            render: (code, row) => (
                <Tag color={STATUS_COLORS[code] || 'default'}>
                    {row.status_name ||
                        statuses[code] ||
                        DEFAULT_STATUSES[code] ||
                        code}
                </Tag>
            )
        },
        {
            title: 'Группа / проект',
            dataIndex: 'group_name',
            width: 180,
            ellipsis: true,
            render: (name, row) =>
                name ||
                (row.group_id ? `#${row.group_id}` : '—')
        },
        {
            title: 'Постановщик',
            dataIndex: 'created_by_name',
            width: 160,
            render: (name, row) => name || row.created_by || '—'
        },
        {
            title: 'Исполнитель',
            dataIndex: 'responsible_name',
            width: 160,
            render: (name, row) =>
                name || row.responsible_id || '—'
        },
        {
            title: 'Крайний срок',
            dataIndex: 'deadline',
            width: 170,
            render: (v, row) => {
                if (!v) return '—';
                const text = new Date(v).toLocaleString('ru-RU');
                if (row.is_overdue) {
                    return (
                        <Text type="danger" strong>
                            {text}
                        </Text>
                    );
                }
                return text;
            }
        },
        {
            title: 'Изменена',
            dataIndex: 'bitrix_changed_at',
            width: 160,
            render: v =>
                v ? new Date(v).toLocaleString('ru-RU') : '—'
        }
    ];

    return (
        <Layout>
            <style>{`
                .bx-task-row-overdue td {
                    background: #fff1f0 !important;
                }
                .bx-task-row-overdue:hover td {
                    background: #ffccc7 !important;
                }
                .bx-task-drawer .ant-drawer-body {
                    padding: 16px 20px 32px;
                }
                .bx-task-drawer .ant-drawer-header {
                    padding: 12px 20px;
                }
            `}</style>
            <Card>
                <Space
                    direction="vertical"
                    size="middle"
                    style={{ width: '100%' }}
                >
                    <Space
                        style={{
                            width: '100%',
                            justifyContent: 'space-between'
                        }}
                        wrap
                    >
                        <div>
                            <Title level={3} style={{ margin: 0 }}>
                                Задачи Bitrix
                            </Title>
                            <Text type="secondary">
                                Видны только задачи, где вы постановщик,
                                исполнитель, соисполнитель или наблюдатель
                            </Text>
                        </div>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={load}
                            loading={loading}
                        >
                            Обновить
                        </Button>
                    </Space>

                    {accessInfo &&
                        !accessInfo.seeAll &&
                        !accessInfo.matched && (
                            <Alert
                                type="warning"
                                showIcon
                                message="Пользователь Bitrix не найден"
                                description="Email в CRM не совпал ни с одним пользователем Bitrix — список пуст. Проверьте email в профиле."
                            />
                        )}

                    {accessInfo?.seeAll ? (
                        <Alert
                            type="info"
                            showIcon
                            message="Режим администратора: показаны все задачи (без ограничения по участникам)"
                        />
                    ) : null}

                    <Space wrap>
                        <Select
                            style={{ width: 180 }}
                            value={scope}
                            options={scopeOptions}
                            onChange={v => {
                                setPage(1);
                                setScope(v);
                                if (v !== 'all') setStatus(undefined);
                            }}
                        />
                        <Select
                            allowClear
                            placeholder="Статус"
                            style={{ width: 200 }}
                            value={status}
                            options={statusOptions}
                            onChange={v => {
                                setPage(1);
                                setStatus(v);
                            }}
                        />
                        <Select
                            style={{ width: 200 }}
                            value={role}
                            options={roleOptions}
                            onChange={v => {
                                setPage(1);
                                setRole(v);
                            }}
                        />
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Группа / проект"
                            style={{ width: 260 }}
                            value={groupId}
                            options={groupOptions}
                            onChange={v => {
                                setPage(1);
                                setGroupId(v);
                            }}
                        />
                        <Input.Search
                            allowClear
                            placeholder="Поиск по названию или ID"
                            prefix={<SearchOutlined />}
                            value={qInput}
                            onChange={e => setQInput(e.target.value)}
                            onSearch={applySearch}
                            style={{ width: 300 }}
                            enterButton="Найти"
                        />
                    </Space>

                    <Table
                        rowKey="id"
                        loading={loading}
                        columns={columns}
                        dataSource={items}
                        rowClassName={row =>
                            row.is_overdue ? 'bx-task-row-overdue' : ''
                        }
                        onRow={row => ({
                            onClick: () => openTask(row.id),
                            style: { cursor: 'pointer' }
                        })}
                        pagination={{
                            current: page,
                            pageSize,
                            total,
                            showSizeChanger: true,
                            showTotal: t => `Всего: ${t}`,
                            onChange: (p, ps) => {
                                setPage(p);
                                setPageSize(ps);
                            }
                        }}
                    />
                </Space>
            </Card>

            <Drawer
                className="bx-task-drawer"
                open={Boolean(openTaskId)}
                onClose={closeTask}
                width="90%"
                destroyOnClose
                placement="right"
                title={drawerTitle}
                styles={{
                    body: {
                        background: '#fafafa'
                    }
                }}
            >
                {openTaskId ? (
                    <BitrixTaskDetailPanel
                        taskId={openTaskId}
                        compactHeader
                    />
                ) : null}
            </Drawer>
        </Layout>
    );
};

export default BitrixTasksPage;
